import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Building2, Loader2 } from 'lucide-react';
import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';
import BasicPagination from '../../components/DataTable/BasicPagination';
import PageSizeSelector from '../../components/DataTable/PageSizeSelector';
import SectionWrapper from '../../components/SectionWrapper/SectionWrapper';
import {
  isArrivalWindowOpen,
  ReservationFiltersBar,
  ReservationFormModal,
  ReservationHeader,
  ReservationList,
  ReservationReasonModal,
  ReservationTable,
} from '../../features/restaurant/reservations';
import useDebounced from '../../hooks/useDebounced';
import useRestaurantReservations from '../../hooks/restaurant/useRestaurantReservations';
import WorkspaceLayout from '../../layout/WorkspaceLayout';
import BranchService from '../../services/branch/BranchService';
import { useAuthUser } from '../../hooks/auth/useAuthUser';
import { IBranchDropdown } from '../../types/branches/branchInterfaces';
import {
  ReservationFilters,
  ReservationPageFilters,
} from '../../types/restaurant/RestaurantReservationTypes';

export const initialReservationFilters: ReservationFilters = {
  activity: 'ACTIVE',
  status: '',
  dateFrom: '',
  dateTo: '',
  tableId: '',
  search: '',
};

const RestaurantReservations = () => {
  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [branches, setBranches] = useState<IBranchDropdown[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [branchError, setBranchError] = useState('');
  const [filters, setFilters] = useState<ReservationFilters>(
    initialReservationFilters
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const debouncedSearch = useDebounced(filters.search, 350);
  const normalizedSearch = filters.search === '' ? '' : debouncedSearch;
  const reservations = useRestaurantReservations(branchId);

  useEffect(() => {
    reservations.resetResults();
    return reservations.resetResults;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setBranchError('');
    const request = canPickBranch
      ? BranchService.fetchDropdown()
      : BranchService.fetchMyBranch().then((mine) => [mine]);

    request
      .then((items) => {
        setBranches(items);
        if (items.length) setBranchId(items[0].id);
      })
      .catch((error: any) =>
        setBranchError(
          error?.response?.data?.message ||
            'No fue posible cargar las sucursales.'
        )
      )
      .finally(() => setLoadingBranches(false));
  }, [canPickBranch]);

  const pageFilters = useMemo<ReservationPageFilters>(() => {
    const status = filters.status || null;
    return {
      active: status
        ? null
        : filters.activity === 'ALL'
        ? null
        : filters.activity === 'ACTIVE',
      status,
      dateFrom: filters.dateFrom || null,
      dateTo: filters.dateTo || null,
      tableId: filters.tableId || null,
      search: normalizedSearch.trim() || null,
      page,
      size: pageSize,
    };
  }, [
    filters.activity,
    filters.status,
    filters.dateFrom,
    filters.dateTo,
    filters.tableId,
    normalizedSearch,
    page,
    pageSize,
  ]);

  useEffect(() => {
    if (branchId) reservations.loadContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  useEffect(() => {
    if (branchId) reservations.load(pageFilters, reservations.page !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, pageFilters]);

  const changeFilters = (next: ReservationFilters) => {
    setFilters(next);
    setPage(0);
  };

  const changeBranch = (id: number) => {
    reservations.resetResults();
    setFilters(initialReservationFilters);
    setPage(0);
    setBranchId(id);
  };

  const previousPage = () => setPage((current) => Math.max(0, current - 1));
  const retry = () =>
    reservations.load(pageFilters, reservations.page !== null);
  const confirmReservation = (
    item: Parameters<typeof reservations.confirmReservation>[0]
  ) => reservations.confirmReservation(item, pageFilters, previousPage);
  const noShow = (item: Parameters<typeof reservations.noShow>[0]) =>
    reservations.noShow(item, pageFilters, previousPage);
  const openOrder = (item: Parameters<typeof reservations.openOrder>[0]) =>
    reservations.openOrder(item, pageFilters, previousPage);

  if (!loadingBranches && !branchError && !branchId) {
    return (
      <WorkspaceLayout>
        <BreadcrumbTwo />
        <SectionWrapper>
          <div className="p-14 text-center">
            <Building2 className="mx-auto h-10 w-10 text-bodydark2" />
            <h1 className="mt-3 text-lg font-semibold text-black dark:text-white">
              No hay sucursales disponibles
            </h1>
            <p className="mt-1 text-sm text-bodydark2">
              Configure una sucursal para administrar reservaciones.
            </p>
          </div>
        </SectionWrapper>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />
      <SectionWrapper>
        <ReservationHeader
          branches={branches}
          branchId={branchId}
          loading={loadingBranches}
          branchError={branchError}
          canPickBranch={canPickBranch}
          creationDisabled={!reservations.settings.reservationsEnabled}
          onBranchChange={changeBranch}
          onCreate={reservations.openCreate}
        />
        {!reservations.settings.reservationsEnabled && branchId && (
          <div className="flex items-start gap-3 border-b border-warning/25 bg-warning/10 px-5 py-4 text-sm text-meta-8 sm:px-7">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <strong>
                Las reservaciones están deshabilitadas en esta sucursal.
              </strong>
              <p className="mt-0.5">
                Puede consultar la agenda, pero no crear nuevas reservaciones
                hasta activarlas en Configuración del restaurante.
              </p>
            </div>
          </div>
        )}
        {branchId && (
          <>
            <ReservationFiltersBar
              filters={filters}
              tables={reservations.tables}
              onChange={changeFilters}
              onRetry={retry}
            />

            {reservations.loading ? (
              <ReservationList
                reservations={[]}
                loading
                error=""
                toleranceMinutes={0}
                onRetry={retry}
                onEdit={reservations.edit}
                onConfirm={confirmReservation}
                onCancel={reservations.setCancelTarget}
                onNoShow={noShow}
                onOpenOrder={openOrder}
                isArrivalWindowOpen={isArrivalWindowOpen}
              />
            ) : reservations.error && !reservations.page ? (
              <ReservationList
                reservations={[]}
                loading={false}
                error={reservations.error}
                toleranceMinutes={0}
                onRetry={retry}
                onEdit={reservations.edit}
                onConfirm={confirmReservation}
                onCancel={reservations.setCancelTarget}
                onNoShow={noShow}
                onOpenOrder={openOrder}
                isArrivalWindowOpen={isArrivalWindowOpen}
              />
            ) : reservations.list.length === 0 ? (
              <ReservationList
                reservations={[]}
                loading={false}
                error=""
                toleranceMinutes={0}
                onRetry={retry}
                onEdit={reservations.edit}
                onConfirm={confirmReservation}
                onCancel={reservations.setCancelTarget}
                onNoShow={noShow}
                onOpenOrder={openOrder}
                isArrivalWindowOpen={isArrivalWindowOpen}
              />
            ) : (
              <div className="relative">
                {reservations.error && (
                  <div className="m-5 flex items-center justify-between gap-3 rounded-lg border border-danger/20 bg-danger/5 px-4 py-3 text-sm text-danger">
                    <span>{reservations.error}</span>
                    <button
                      type="button"
                      onClick={retry}
                      className="font-semibold underline"
                    >
                      Reintentar
                    </button>
                  </div>
                )}
                {reservations.updating && (
                  <div className="absolute right-5 top-3 z-10 flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-primary shadow dark:bg-boxdark">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />{' '}
                    Actualizando
                  </div>
                )}
                <ReservationTable
                  reservations={reservations.list}
                  toleranceMinutes={
                    reservations.settings.reservationToleranceMinutes
                  }
                  onEdit={reservations.edit}
                  onConfirm={confirmReservation}
                  onCancel={reservations.setCancelTarget}
                  onNoShow={noShow}
                  onOpenOrder={openOrder}
                  isArrivalWindowOpen={isArrivalWindowOpen}
                />
                <ReservationList
                  reservations={reservations.list}
                  loading={false}
                  error=""
                  toleranceMinutes={
                    reservations.settings.reservationToleranceMinutes
                  }
                  onRetry={retry}
                  onEdit={reservations.edit}
                  onConfirm={confirmReservation}
                  onCancel={reservations.setCancelTarget}
                  onNoShow={noShow}
                  onOpenOrder={openOrder}
                  isArrivalWindowOpen={isArrivalWindowOpen}
                />
              </div>
            )}

            {reservations.page && reservations.page.totalElements > 0 && (
              <div className="flex flex-col gap-4 border-t border-stroke px-5 pb-5 dark:border-strokedark sm:flex-row sm:items-end sm:justify-between">
                <p className="pb-2 text-sm font-medium text-bodydark2">
                  {reservations.page.totalElements.toLocaleString('es-SV')}{' '}
                  reservaciones encontradas
                </p>
                <BasicPagination
                  currentPage={reservations.page.number}
                  totalPages={reservations.page.totalPages}
                  onPageChange={setPage}
                />
                <PageSizeSelector
                  pageSize={pageSize}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(0);
                  }}
                />
              </div>
            )}
          </>
        )}
      </SectionWrapper>
      <ReservationFormModal
        open={reservations.modalOpen}
        form={reservations.form}
        errors={reservations.errors}
        customers={reservations.customers}
        availability={reservations.availability}
        availabilityLoading={reservations.availabilityLoading}
        saving={reservations.saving}
        allowGuests={reservations.settings.allowGuestReservations}
        allowMultipleTables={reservations.settings.allowMultipleTables}
        allowOverbooking={reservations.settings.allowOverbooking}
        maximumAdvanceDays={reservations.settings.maximumAdvanceDays}
        selectedCapacity={reservations.selectedCapacity}
        onClose={reservations.closeModal}
        onChange={reservations.updateField}
        onSubmit={() => reservations.save(pageFilters, previousPage)}
      />
      <ReservationReasonModal
        open={Boolean(reservations.cancelTarget)}
        saving={reservations.saving}
        onClose={() => reservations.setCancelTarget(null)}
        onSubmit={(reason) =>
          reservations.cancelReservation(reason, pageFilters, previousPage)
        }
      />
    </WorkspaceLayout>
  );
};

export default RestaurantReservations;
